import branch from 'recompose/branch';
import renderComponent from 'recompose/renderComponent';
import { get, filter, map, isEmpty } from 'lodash';
import withPropsOnChange from 'recompose/withPropsOnChange';
import { reduxForm, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';
import withProps from 'recompose/withProps';
import { connect } from 'react-redux';
import { t } from '../../../../../modules/infra/i18n';

import withPermissions from '../../../../auth/utils/withPermissions';
import LoyaltyForm from '../component/LoyaltyForm';
import { syncValidateData } from '../../../../common/validator';
import { createLoyalty, updateLoyalty, fetchLoyalty } from '../actions';
import { getActiveType, getActiveShop, isBrandShop, activeShopIsOnlyShopInBrand } from '../../../../auth/selectors';
import fetchOnUpdate from '../../../../common/decorators/fetchOnUpdate';
import withNotifications from '../../../../common/withNotifications';
import { getLoyaltyProgramInitialValues } from '../utils';
import withActiveUserIdAndType from '../../../../common/hoc/withActiveUserIdAndType';
import { getLoyaltyProgram } from '../selector';
import NoLoyaltyProgram from '../component/NoLoyaltyProgram';

const LOYALTY_FORM = 'loyaltyForm';

const resetFields = (props) => {
  if (props.rewardType && props.rewardType !== props.initialValues.rewardType) {
    props.change('goalCounter', '');
    props.change('rewardAmount', 0);
    props.change('minTransactionAmount', 0);
  }
};

const createSchema = Joi.object().keys({
  minTransactionAmount: Joi.number().min(0).integer().optional().default(0),
  goalCounter: Joi.number().min(0).integer().required(),
  rewardAmount: Joi.number().min(0).integer().required(),
  enabled: Joi.boolean().required(),
  includeProductSales: Joi.boolean().optional().allow(null),
  includeTips: Joi.boolean().optional().allow(null),
});

const validate = (values) => syncValidateData(values, createSchema);

const form = reduxForm({
  form: LOYALTY_FORM,
  enableReinitialize: true,
  validate,
  onSubmitSuccess: (_values, _dispatch, props) => {
    props.addNotification({
      title: t('settings:loyalty.success_title'),
      level: 'success',
      message: t('settings:loyalty.changes_saved_successfully'),
    });
  },
});

const prepareForSave = (data) => {
  if (Array.isArray(data.shops)) {
    data.shopIds = map(filter(data.shops, 'assigned'), 'id');
  }
  return data;
};

const addCreateAndUpdateHanders = withHandlers({
  update: (props) => (data) => props.updateLoyalty(props.activeId, props.activeType, prepareForSave(data)),
  create: (props) => (data) => props.createLoyalty(props.activeId, props.activeType, prepareForSave(data)),
});

const addHandlers = withHandlers({
  onSubmit: (props) => (data) => {
    if (data.id) {
      return props.update(data);
    }

    return props.create(data);
  },
});

const prepareFetchParams = withProps((props) => ({
  params: {
    activeId: props.activeId,
    activeType: props.activeType,
  },
  actions: {
    fetchLoyalty: props.fetchLoyalty,
  },
}));

const fetch = fetchOnUpdate(['activeId', 'activeType'], (params, actions) =>
  actions.fetchLoyalty(params.activeId, params.activeType)
);

const selector = formValueSelector(LOYALTY_FORM);

const mapStateToProps = (state) => {
  const initialValues = getLoyaltyProgramInitialValues(state);
  const onlyShopInBrand = activeShopIsOnlyShopInBrand(state);
  return {
    initialValues,
    loyaltyProgram: getLoyaltyProgram(state),
    rewardType: selector(state, 'rewardType'),
    shops: selector(state, 'shops'),
    onlyShopInBrand,
    canModify: getActiveType(state) !== 'shop' || !getActiveShop(state).brandId || onlyShopInBrand,
    isBrandPromo: get(initialValues, 'ownerType') === 'brand',
    isBrandShop: isBrandShop(state),
  };
};

const mapDispatchToProps = {
  createLoyalty,
  updateLoyalty,
  fetchLoyalty,
};

export default compose(
  withActiveUserIdAndType(),
  withPermissions,
  connect(mapStateToProps, mapDispatchToProps),
  withNotifications,
  addCreateAndUpdateHanders,
  addHandlers,
  form,
  prepareFetchParams,
  fetch,
  branch(
    (props) => isEmpty(props.loyaltyProgram) && props.isBrandShop && !props.canModify,
    renderComponent(NoLoyaltyProgram)
  ),
  withPropsOnChange(['rewardType'], resetFields)
)(LoyaltyForm);
